import {
  getGoogleClientId,
  getGoogleRedirectUri,
  getMicrosoftClientId,
  getMicrosoftRedirectUri,
} from './oauthConfigService';
import { GOOGLE_UNIFIED_SCOPES } from './googleScopes';
import type { EmailConnection, EmailProvider, EmailOAuthCallbackData } from '../types/email';

const GOOGLE_OAUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

const MICROSOFT_OAUTH_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const MICROSOFT_EMAIL_SCOPES = 'Mail.ReadWrite Mail.Send User.Read offline_access';

const MAX_EMAIL_ACCOUNTS = 30;

export const emailConnectionService = {
  initiateGoogleOAuth: async (clientId: string, userId: string): Promise<string> => {
    const googleClientId = await getGoogleClientId();
    const redirectUri = await getGoogleRedirectUri('email');

    if (!googleClientId || !redirectUri) {
      throw new Error('Google Email OAuth not configured. Check oauth_config table.');
    }

    const state = JSON.stringify({ client_id: clientId, user_id: userId });

    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: GOOGLE_UNIFIED_SCOPES,
      access_type: 'offline',
      prompt: 'consent',
      state,
    });

    return `${GOOGLE_OAUTH_URL}?${params.toString()}`;
  },

  initiateMicrosoftOAuth: async (clientId: string, userId: string): Promise<string> => {
    const microsoftClientId = await getMicrosoftClientId();
    const redirectUri = await getMicrosoftRedirectUri('email');

    if (!microsoftClientId || !redirectUri) {
      throw new Error('Microsoft Email OAuth not configured. Check oauth_config table.');
    }

    const state = JSON.stringify({ client_id: clientId, user_id: userId });

    const params = new URLSearchParams({
      client_id: microsoftClientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: MICROSOFT_EMAIL_SCOPES,
      response_mode: 'query',
      prompt: 'consent',
      state,
    });

    return `${MICROSOFT_OAUTH_URL}?${params.toString()}`;
  },

  handleGoogleOAuthCallback: async (
    code: string,
    clientId: string,
    userId: string
  ): Promise<EmailOAuthCallbackData> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = await getGoogleRedirectUri('email');
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/google-email-oauth-callback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify({
            code,
            client_id: clientId,
            user_id: userId,
            redirect_uri: redirectUri,
          }),
        }
      );

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        return { success: false, error: 'Invalid response from server' };
      }

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to connect Gmail' };
      }

      return { success: true, connection: data.connection };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  handleMicrosoftOAuthCallback: async (
    code: string,
    clientId: string,
    userId: string
  ): Promise<EmailOAuthCallbackData> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = await getMicrosoftRedirectUri('email');
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/microsoft-email-oauth-callback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify({
            code,
            client_id: clientId,
            user_id: userId,
            redirect_uri: redirectUri,
          }),
        }
      );

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        return { success: false, error: 'Invalid response from server' };
      }

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to connect Microsoft email' };
      }

      return { success: true, connection: data.connection };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getEmailConnections: async (clientId: string): Promise<EmailConnection[]> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-email-connections`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify({ client_id: clientId }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('Error fetching email connections:', data.error);
        return [];
      }

      return data.connections || [];
    } catch (error) {
      console.error('Error getting email connections:', error);
      return [];
    }
  },

  disconnectEmail: async (
    connectionId: string,
    clientId: string,
    provider: EmailProvider
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const endpoint = provider === 'microsoft'
        ? 'disconnect-microsoft-email'
        : 'disconnect-google-email';

      const response = await fetch(
        `${supabaseUrl}/functions/v1/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify({ connection_id: connectionId, client_id: clientId }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to disconnect email' };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  deleteEmailConnection: async (
    connectionId: string,
    clientId: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/delete-email-connection`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify({ connection_id: connectionId, client_id: clientId }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to delete email connection' };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  maxAccounts: MAX_EMAIL_ACCOUNTS,
};
